
import Layout from "@/components/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail, MessageCircle } from "lucide-react";

const FAQs = () => {
  const generalFaqs = [
    {
      question: "What is JUV and what services do you offer?",
      answer: "JUV is an innovative aesthetic healthcare platform that combines dental offices with medspa services. We offer a comprehensive range of treatments including injectables (Botox, fillers), skin treatments (chemical peels, facials), body contouring, wellness services, and dental aesthetics."
    },
    {
      question: "How does the JUV membership work?",
      answer: "Our Glow membership provides monthly credits that can be used for any JUV treatment. Members enjoy exclusive pricing, priority booking, rollover credits, and special events. Choose from Glow ($99), Luminate ($199), or Radiate ($299) plans based on your needs."
    },
    {
      question: "Are your treatments safe?",
      answer: "Absolutely. All treatments are performed by licensed medical professionals in clinical settings. We follow strict safety protocols and use only FDA-approved products and devices. Every patient receives a thorough consultation to ensure treatment suitability."
    },
    {
      question: "How do I book an appointment?",
      answer: "You can book through our website, mobile app, or by calling any of our locations. Members get priority booking and can easily manage appointments through their JUV portal."
    }
  ];

  const membershipFaqs = [
    {
      question: "Can I use my credits for any treatment?",
      answer: "Yes! Your monthly Glow credits can be applied to any JUV treatment or service. Credits roll over for up to 12 months, giving you flexibility in how you use them."
    },
    {
      question: "What happens if I cancel my membership?",
      answer: "You can cancel anytime with 30 days notice. Any unused credits remain available for 60 days after cancellation, and you'll retain member pricing during that period."
    },
    {
      question: "Do you offer family or couples memberships?",
      answer: "We offer special rates for multiple memberships under one account. Contact us to learn about family and couples membership options."
    },
    {
      question: "Can I upgrade or downgrade my membership plan?",
      answer: "Yes, you can change your membership level at any time. Changes take effect on your next billing cycle, and any credit differences are prorated."
    }
  ];

  const treatmentFaqs = [
    {
      question: "How long do results typically last?",
      answer: "Results vary by treatment: Botox lasts 3-4 months, dermal fillers 6-18 months depending on type, chemical peels show immediate improvement with ongoing benefits, and skin treatments typically require a series for optimal results."
    },
    {
      question: "Is there any downtime with treatments?",
      answer: "Most treatments have minimal to no downtime. Injectables may have slight swelling or bruising for 1-2 days. Chemical peels may cause peeling for 3-7 days. We'll discuss expected downtime during your consultation."
    },
    {
      question: "How do I prepare for my treatment?",
      answer: "Preparation varies by treatment. Generally, avoid blood thinners, alcohol, and excessive sun exposure before treatment. We provide detailed pre-care instructions specific to your treatment plan."
    },
    {
      question: "What should I expect during my first visit?",
      answer: "Your first visit includes a comprehensive consultation to discuss your goals, medical history, and treatment options. We'll create a personalized treatment plan and can often begin same-day treatments if desired."
    }
  ];

  return (
    <Layout>
      <section className="py-20 bg-gradient-to-br from-juv-cream-white to-white">
        <div className="container mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center max-w-4xl mx-auto mb-20">
            <h1 className="text-4xl md:text-6xl font-bold text-juv-deep-chestnut mb-6 font-juv-display">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-juv-deep-chestnut/70 leading-relaxed">
              Find answers to common questions about JUV treatments, memberships, and services.
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="max-w-4xl mx-auto space-y-12">
            {/* General FAQs */}
            <div>
              <h2 className="text-2xl font-bold text-juv-deep-chestnut mb-6 font-juv-heading">
                General Questions
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {generalFaqs.map((faq, index) => (
                  <AccordionItem key={index} value={`general-${index}`} className="border border-juv-rose-clay/20 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-juv-deep-chestnut hover:text-juv-terracotta">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-juv-deep-chestnut/70 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Membership FAQs */}
            <div>
              <h2 className="text-2xl font-bold text-juv-deep-chestnut mb-6 font-juv-heading">
                Membership Questions
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {membershipFaqs.map((faq, index) => (
                  <AccordionItem key={index} value={`membership-${index}`} className="border border-juv-rose-clay/20 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-juv-deep-chestnut hover:text-juv-terracotta">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-juv-deep-chestnut/70 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Treatment FAQs */}
            <div>
              <h2 className="text-2xl font-bold text-juv-deep-chestnut mb-6 font-juv-heading">
                Treatment Questions
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {treatmentFaqs.map((faq, index) => (
                  <AccordionItem key={index} value={`treatment-${index}`} className="border border-juv-rose-clay/20 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-juv-deep-chestnut hover:text-juv-terracotta">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-juv-deep-chestnut/70 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-juv-deep-chestnut text-center mb-12 font-juv-display">
              Still Have Questions?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <Card className="bg-gradient-to-br from-white to-juv-soft-blush/30 border-juv-rose-clay/20 hover:shadow-luxury transition-all duration-300">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-juv-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-juv-deep-chestnut">Call Us</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-juv-deep-chestnut/70 mb-4">
                    Speak with our team directly
                  </p>
                  <Button variant="outline" className="border-juv-terracotta text-juv-terracotta hover:bg-juv-terracotta hover:text-white">
                    (555) 588-4569
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-white to-juv-soft-blush/30 border-juv-rose-clay/20 hover:shadow-luxury transition-all duration-300">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-juv-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-juv-deep-chestnut">Email Us</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-juv-deep-chestnut/70 mb-4">
                    Get detailed answers via email
                  </p>
                  <Button variant="outline" className="border-juv-terracotta text-juv-terracotta hover:bg-juv-terracotta hover:text-white">
                    hello@juv.com
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-white to-juv-soft-blush/30 border-juv-rose-clay/20 hover:shadow-luxury transition-all duration-300">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-juv-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-juv-deep-chestnut">Live Chat</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-juv-deep-chestnut/70 mb-4">
                    Chat with us in real-time
                  </p>
                  <Button variant="outline" className="border-juv-terracotta text-juv-terracotta hover:bg-juv-terracotta hover:text-white">
                    Start Chat
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQs;
